import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { Logo } from '../components/Logo';
import { Shield, Key, Mail, Zap, ArrowLeft } from 'lucide-react';
import { BRAND_NAME } from '../brand';

const LoginPage: React.FC = () => {
    const { signInWithGoogle, loading, session } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { bootstrap } = useAppBootstrap();

    useEffect(() => {
        if (session && bootstrap) {
            const target = bootstrap.needs_onboarding ? '/onboarding' : '/app/today';
            navigate({ pathname: target, search: location.search }, { replace: true });
        }
    }, [session, bootstrap, navigate, location.search]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center px-4 py-12">
            {/* Background orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 -left-48 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/3 -right-48 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
            </div>

            <div data-testid="login-page" className="max-w-md w-full relative z-10">
                <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl">
                    {/* Logo + Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <Logo variant="wordmark" size={24} appearance="dark" />
                    </div>

                    <div className="space-y-2 mb-8">
                        <h1 className="text-2xl font-bold">Welcome back</h1>
                        <p className="text-sm text-slate-400">
                            Sign in with Google. Gmail access is a separate step — you stay in control.
                        </p>
                    </div>

                    {/* Google Sign In Button */}
                    <button
                        data-testid="login-continue-google"
                        onClick={signInWithGoogle}
                        disabled={loading}
                        className="w-full px-5 py-4 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.01] transition-all duration-200 disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                Connecting...
                            </span>
                        ) : (
                            <>
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Continue with Google
                            </>
                        )}
                    </button>

                    {/* Trust Badges */}
                    <div className="grid grid-cols-2 gap-2 mt-6">
                        <Badge icon={<Shield className="w-3.5 h-3.5" />} title="No passwords" desc="OAuth + JWT" />
                        <Badge icon={<Key className="w-3.5 h-3.5" />} title="Server-verified" desc="JWKS validation" />
                        <Badge icon={<Mail className="w-3.5 h-3.5" />} title="Scoped Gmail" desc="Separate consent" />
                        <Badge icon={<Zap className="w-3.5 h-3.5" />} title="Setup" desc={`If mail doesn't show, ${BRAND_NAME} says so.`} />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10 text-xs text-slate-500">
                        <button
                            data-testid="login-back"
                            onClick={() => navigate('/')}
                            className="flex items-center gap-1 hover:text-teal-400 transition-colors"
                        >
                            <ArrowLeft className="w-3 h-3" />
                            Back
                        </button>
                        <span>We never store your password</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Badge = ({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) => (
    <div className="rounded-xl border border-white/5 bg-white/5 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-slate-100">
            <span className="text-teal-400">{icon}</span>
            <span className="text-xs font-medium">{title}</span>
        </div>
        <div className="text-[10px] text-slate-500 mt-0.5">{desc}</div>
    </div>
);

export default LoginPage;
